import {Component, OnDestroy, OnInit} from '@angular/core';
import {
  BaseComponent,
  WmsDialogBodyComponent,
  WmsDialogFooterComponent,
  WmsDialogHeaderComponent,
  WmsMessageComponent
} from "@wms/components";
import {DialogService, NotificationService, ProjectUpdatesService} from "@wms/services";
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {VcsService} from "@wms/app/vcs/services/vcs.service";
import {getVcsAccountIconUrl, getVCSGroupData, toPascalCase} from "@wms/core";
import {replace} from "@wm/core";

@Component({
  selector: 'wms-vcs-user-credentials',
  templateUrl: './wms-vcs-user-credentials.component.html',
  styleUrls: ['./wms-vcs-user-credentials.component.less'],
  imports: [
    WmsDialogBodyComponent,
    WmsDialogFooterComponent,
    WmsDialogHeaderComponent,
    WmsMessageComponent,
    CommonModule,
    FormsModule
  ],
  standalone: true
})
export class WmsVcsUserCredentialsComponent extends BaseComponent implements OnInit, OnDestroy {
  message: any ='';
  vcsInfo: any;
  vcsAccountId: any;
  groupLabel: string;
  groupName: string;
  accessTokenLink: string;
  githubAppsAuth = false;

  private oauthPopup: Window | null = null;
  private oauthPollId: number | null = null;

  constructor(private dialogService: DialogService, private vcsService: VcsService, private projectUpdatesService: ProjectUpdatesService, private notificationService: NotificationService) {
    super();
    this.groupLabel = '';
  }

  ngOnInit() {
    this.vcsInfo      = this.vcsService.dialogParams.vcsInfo;
    this.vcsAccountId = this.vcsService.dialogParams.vcsAccountId;
    this.githubAppsAuth = !!this.vcsService.dialogParams.githubAppsAuth;
    this.message = this.githubAppsAuth ? null : this.vcsInfo.developerKeysEnabled && this.projectUpdatesService.invalidRepoBranchInfo ? { 'type': 'error', 'caption': `${toPascalCase(this.vcsInfo.vcsAccountType)} ${this.locale.MESSAGE_VCS_USER_CREDENTIALS_NOT_FOUND_UPDATE}` } : null;
  }

  override ngOnDestroy(): void {
    this.clearOauthPoll();
    super.ngOnDestroy();
  }

  closeModal() {
    this.clearOauthPoll();
    try {
      if (this.oauthPopup && !this.oauthPopup.closed) {
        this.oauthPopup.close();
      }
    } catch { /* ignore */ }
    this.oauthPopup = null;
    this.dialogService.close('vcsUserCredentials');
  }

  getVcsIcon() {
    return getVcsAccountIconUrl(this.vcsInfo.vcsAccountType, this.vcsService.baseURL);
  }

  getGroupName() {
    let groupData = getVCSGroupData(this.vcsInfo.vcsAccountType, this.vcsInfo.properties);
    this.groupName = groupData.groupName;
    this.groupLabel = groupData.groupLabel;
    this.accessTokenLink  = groupData.accessTokenLink;
    return groupData.groupName;
  }

  authorizeGithubApps(): void {
    if (this.vcsAccountId == null || this.vcsAccountId === '') {
      this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, this.locale.MESSAGE_GITHUB_APPS_AUTHORIZE_URL_MISSING);
      return;
    }
    this.projectService.getGithubAppsAuthorizationUrl(this.vcsAccountId).then(
      (body: any) => {
        const url = typeof body === 'string' ? body : body?.result;
        if (!url || typeof url !== 'string') {
          this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, this.locale.MESSAGE_GITHUB_APPS_AUTHORIZE_URL_MISSING);
          return;
        }
        const popup = window.open(url, 'githubAppsOAuth', 'width=600,height=720,scrollbars=yes');
        if (!popup) {
          this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, this.locale.MESSAGE_GITHUB_APPS_POPUP_BLOCKED);
          return;
        }
        this.clearOauthPoll();
        this.oauthPopup = popup;
        var callbackPath = '/edn-services/rest/github-apps/oauth/callback';
        this.oauthPollId = window.setInterval(() => {
          const w = this.oauthPopup;
          if (!w) {
            this.clearOauthPoll();
            return;
          }
          if (w.closed) {
            this.oauthPopup = null;
            this.clearOauthPoll();
            return;
          }
          try {
              if (w.location.href.includes(callbackPath)) {
                  const popupUrl = new URL(w.location.href);
                  const code = popupUrl.searchParams.get('code');
                  const state = popupUrl.searchParams.get('state');
                  this.clearOauthPoll();
                  if (code && state) {
                      this.projectService.getGithubAppsCallbackurl(code, state).then((data: any) => {
                              if (data.status === 'SUCCESS') {
                                  this.notificationService.show('success', this.locale.MESSAGE_SUCCESS_TITLE, data.message);
                              } else if (data.status === 'FAILURE') {
                                  this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, data.message);
                              }
                          })
                          .catch((error: any) => {
                              if(error.status === 500) {
                                  this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, 'Internal Server Error');
                              } else if(error.status === 400) {
                                  this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, 'Invalid Authorization Request');
                              }else{
                                  this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, 'Failed to verify OAuth status');
                              }
                          });
                   }
                  try { w.close(); } catch {}
                  this.oauthPopup = null;
                  this.dialogService.close('vcsUserCredentials');
              }
          } catch { /* cross-origin until same host */ }
        }, 400);
      },
      (error: any) => {
        const err = error?.error?.errors?.error?.[0];
        this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, err ? replace(err.message, err.parameters, true) : this.locale.MESSAGE_GITHUB_APPS_AUTHORIZE_URL_MISSING);
      },
    );
  }

  private clearOauthPoll(): void {
    if (this.oauthPollId != null) {
      clearInterval(this.oauthPollId);
      this.oauthPollId = null;
    }
  }

  updateUserVCSCredentials() {
    let params = {
      vcsAccountId: this.vcsAccountId,
      data : {
        "userName"    :this.vcsInfo.username,
        "accessToken" :this.vcsInfo.accessToken || ''
      }
    }
    this.projectService.updateUserVCSCredentials(params).then((response: any) => {
      // show success msg and then close the existing dialog and trigger successCallback
      this.message = { 'type': 'success', 'caption': this.locale.MSG_USER_VCS_DETAILS_UPDATED };
      setTimeout( () => {
        this.dialogService.close('vcsUserCredentials');
      }, 1500);
    }, (error: any) => {
      let errorObject = error.error.errors.error[0];
      this.message = { 'type'   : 'error', 'caption': this.locale.MSG_ERR_USER_VCS_UPDATE_FAILED || errorObject.messageKey };
    });
  }
}
